import { useParams } from 'react-router-dom';
import { ArrowLeft, MoreSmall } from '@peacecover/icon';
import { Virtuoso } from 'react-virtuoso';
import {
  useGetSceneContents,
  flattenSceneContents
} from '../../domains/scene/ui/hooks/useGetSceneContents';
import { useHandleHistoryBack } from '../../utils/useHandleHistoryBack';
import { ReactComponent as GhostProfileIcon } from '../../icons/thumbnail.ghost.profile.svg';
import type { components } from '../../types/schema';

type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];

const ScenePreviewDetailPage = () => {
  const { sceneId } = useParams();
  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } = useGetSceneContents(
    sceneId as string
  );
  const handleHistoryBack = useHandleHistoryBack();
  const contents = flattenSceneContents(data);

  const loadMore = () => {
    if (isFetching || !hasNextPage) return;
    fetchNextPage();
  };

  return (
    <div className="flex min-h-screen flex-col overflow-hidden pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-background-dark">
      <header className="flex flex-shrink-0 items-center justify-between px-[16px] py-[8px] text-main-dark-1">
        <button type="button" onClick={handleHistoryBack}>
          <ArrowLeft size={24} />
        </button>
        <p className="text-[17px] font-bold leading-[20px] text-center">제목</p>
        <MoreSmall size={24} />
      </header>
      <main className="relative flex flex-1 flex-col min-h-0">
        {isLoading || contents.length === 0 ? null : (
          <div className="absolute inset-0 min-h-0">
            <Virtuoso
              overscan={300}
              defaultItemHeight={80}
              style={{ height: '100%' }}
              data={contents}
              endReached={loadMore}
              atBottomThreshold={200}
              itemContent={(index, content: SceneContent) => (
                <SceneContentItem key={getContentKey(content, index)} content={content} />
              )}
            />
          </div>
        )}
      </main>
    </div>
  );
};

export default ScenePreviewDetailPage;

const getContentKey = (content: SceneContent, index: number): string => {
  if ('sceneThreadId' in content && content.sceneThreadId) return content.sceneThreadId;
  if ('sceneChatId' in content && (content as { sceneChatId?: string }).sceneChatId) {
    return (content as { sceneChatId: string }).sceneChatId;
  }
  return `content-${index}`;
};

const SceneContentItem = ({ content }: { content: SceneContent }) => {
  const speaker = 'speaker' in content ? content.speaker : null;
  const text = 'text' in content ? content.text : '';

  return (
    <div className="px-[22px] py-[15px] flex gap-[10px] items-stretch">
      <div className="flex flex-col items-center gap-[8px]">
        <GhostProfileIcon width={26} height={26} />
        <div className="w-[2px] flex-1 mb-[-20px] rounded-[16px] bg-[rgba(255,255,255,0.13)]" />
      </div>
      <div className="flex flex-col items-start gap-[5px] flex-1">
        <p className="text-[14px] font-semibold leading-[17px] text-separator-light-1">
          {speaker ?? '익명의 제보자'}
        </p>
        <p className="text-[15px] leading-[19px] text-pure-white">{text}</p>
      </div>
    </div>
  );
};
